import { useState, useEffect, useCallback, useRef } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader, ToggleSwitch } from '../components/ui';
import MitarbeiterListItem from '../components/MitarbeiterListItem';
import { CreditCard, Search, Eye, Download, FileText, CheckCircle, XCircle, Users, Camera, Sliders, RotateCcw, Save, Star, Trash2, Plus, Grid3X3, ChevronDown, ChevronRight } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

// ─── Tab bar ──────────────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div className="flex rounded-[10px] p-[3px] border" style={{ background: 'var(--bg-inset, #F8F9FC)', borderColor: 'var(--border-card, #E5E7EB)' }}>
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => onChange(tab.key)}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium rounded-lg transition-all ${active === tab.key ? 'bg-[var(--bg-card,#fff)] shadow-sm' : ''}`}
          style={{ color: active === tab.key ? 'var(--text-primary, #1A1A2E)' : 'var(--text-tertiary, #6B7280)' }}>
          {tab.icon}{tab.label}
        </button>
      ))}
    </div>
  );
}

// ─── Collapsible section ──────────────────────────────────────
function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-left"
        style={{ color: 'var(--text-primary, #1A1A2E)', background: 'var(--bg-inset, #F8F9FC)' }}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {title}
      </button>
      {open && <div className="px-4 py-3 space-y-3">{children}</div>}
    </div>
  );
}

// ─── Slider field ─────────────────────────────────────────────
function SliderField({ label, value, min, max, step = 1, onChange, unit = 'px', disabled = false }) {
  return (
    <div className={disabled ? 'opacity-40 pointer-events-none' : ''}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</label>
        <span className="text-xs font-mono" style={{ color: 'var(--text-tertiary, #6B7280)' }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-[#338edc]"
        style={{ background: `linear-gradient(to right, #338edc 0%, #338edc ${((value - min) / (max - min)) * 100}%, var(--border-default, #E5E7EB) ${((value - min) / (max - min)) * 100}%, var(--border-default, #E5E7EB) 100%)` }} />
    </div>
  );
}

// ─── Color field ──────────────────────────────────────────────
function ColorField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs font-medium flex-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</label>
      <div className="flex items-center gap-1.5 border rounded-lg px-2 py-1" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
        <input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)}
          className="w-5 h-5 rounded border-0 cursor-pointer" style={{ padding: 0 }} />
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
          className="w-[70px] text-xs font-mono bg-transparent outline-none"
          style={{ color: 'var(--text-primary, #1A1A2E)' }} />
      </div>
    </div>
  );
}

// ─── Text field ───────────────────────────────────────────────
function TextField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</label>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:border-[#338edc]"
        style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)', background: 'var(--bg-card, #fff)' }} />
    </div>
  );
}

// ─── Textarea field ───────────────────────────────────────────
function TextareaField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</label>
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2}
        className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none resize-none focus:border-[#338edc]"
        style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)', background: 'var(--bg-card, #fff)' }} />
    </div>
  );
}

// ─── Toggle field ─────────────────────────────────────────────
function ToggleField({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <label className="text-xs font-medium" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</label>
      <ToggleSwitch checked={value} onChange={onChange} />
    </div>
  );
}

// ─── Select field ─────────────────────────────────────────────
function SelectField({ label, value, options, onChange }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>{label}</label>
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:border-[#338edc]"
        style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)', background: 'var(--bg-card, #fff)' }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  LAYOUT EDITOR
// ═══════════════════════════════════════════════════════════════

function LayoutEditor({ t }) {
  const [config, setConfig] = useState(null);
  const [layouts, setLayouts] = useState([]);
  const [activeLayoutId, setActiveLayoutId] = useState(null);
  const [layoutName, setLayoutName] = useState('');
  const [previewImg, setPreviewImg] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMa, setPreviewMa] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const debounceRef = useRef(null);

  // Load initial data
  useEffect(() => {
    // Load layouts
    fetch(`${API}/api/ausweis/layout/list`, { credentials: 'include' })
      .then(r => r.json()).then(data => { if (Array.isArray(data)) setLayouts(data); }).catch(() => {});

    // Load active layout
    fetch(`${API}/api/ausweis/layout/aktiv`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        if (data?.config) {
          setConfig(data.config);
          setActiveLayoutId(data.id);
          setLayoutName(data.name);
        }
      }).catch(() => {});

    // Load employees for preview
    fetch(`${API}/api/ausweis/mitarbeiter`, { credentials: 'include' })
      .then(r => r.json()).then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setEmployees(data);
          // Pick first with photo+barcode, or first
          const best = data.find(e => e.has_photo && e.has_barcode) || data[0];
          setPreviewMa(best);
        }
      }).catch(() => {});
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }
  }, [toast]);

  // Generate preview with debounce
  const generatePreview = useCallback((cfg, maId) => {
    if (!cfg || !maId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await fetch(`${API}/api/ausweis/vorschau`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mitarbeiter_id: maId, config: cfg }),
        });
        const data = await res.json();
        if (data.preview) setPreviewImg(data.preview);
      } catch {}
      setPreviewLoading(false);
    }, 400);
  }, []);

  // Trigger preview on config or MA change
  useEffect(() => {
    if (config && previewMa) generatePreview(config, previewMa.id);
  }, [config, previewMa, generatePreview]);

  // Update nested config field
  const updateField = useCallback((section, field, value) => {
    setConfig(prev => {
      if (!prev) return prev;
      return { ...prev, [section]: { ...prev[section], [field]: value } };
    });
  }, []);

  // Save current layout
  const saveLayout = async () => {
    if (!config || !activeLayoutId) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/ausweis/layout/${activeLayoutId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: layoutName, config }),
      });
      setToast(t('ausweisEditor.saved', 'Layout gespeichert'));
      // Refresh list
      const res = await fetch(`${API}/api/ausweis/layout/list`, { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) setLayouts(data);
    } catch { setToast(t('ausweisEditor.saveError', 'Fehler beim Speichern')); }
    setSaving(false);
  };

  // Create new layout
  const createLayout = async () => {
    try {
      const res = await fetch(`${API}/api/ausweis/layout`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: `Layout ${layouts.length + 1}`, config }),
      });
      const data = await res.json();
      if (data?.id) {
        setActiveLayoutId(data.id);
        setLayoutName(data.name);
        setConfig(data.config);
        setToast(t('ausweisEditor.created', 'Neues Layout erstellt'));
        const listRes = await fetch(`${API}/api/ausweis/layout/list`, { credentials: 'include' });
        const listData = await listRes.json();
        if (Array.isArray(listData)) setLayouts(listData);
      }
    } catch {}
  };

  // Set active
  const setActive = async (id) => {
    try {
      await fetch(`${API}/api/ausweis/layout/${id}/set-aktiv`, {
        method: 'POST', credentials: 'include',
      });
      setToast(t('ausweisEditor.activated', 'Als Standard gesetzt'));
      const res = await fetch(`${API}/api/ausweis/layout/list`, { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) setLayouts(data);
    } catch {}
  };

  // Delete layout
  const deleteLayout = async (id) => {
    try {
      const res = await fetch(`${API}/api/ausweis/layout/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setToast(t('ausweisEditor.deleted', 'Layout gelöscht'));
        const listRes = await fetch(`${API}/api/ausweis/layout/list`, { credentials: 'include' });
        const listData = await listRes.json();
        if (Array.isArray(listData)) setLayouts(listData);
      }
    } catch {}
  };

  // Load a layout
  const loadLayout = async (id) => {
    try {
      const res = await fetch(`${API}/api/ausweis/layout/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (data?.config) {
        setConfig(data.config);
        setActiveLayoutId(data.id);
        setLayoutName(data.name);
      }
    } catch {}
  };

  // Reset to default
  const resetToDefault = async () => {
    try {
      const res = await fetch(`${API}/api/ausweis/layout/default`, { credentials: 'include' });
      const data = await res.json();
      if (data?.config) {
        setConfig(data.config);
        setToast(t('ausweisEditor.reset', 'Auf Standard zurückgesetzt'));
      }
    } catch {}
  };

  if (!config) {
    return <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const g = config.gradient || {};
  const logo = config.logo || {};
  const ht = config.headerText || {};
  const photo = config.photo || {};
  const name = config.name || {};
  const pnr = config.personalnummer || {};
  const abt = config.abteilung || {};
  const bc = config.barcode || {};
  const ft = config.footer || {};

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* ─── Left: Preview ───────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {/* Employee selector */}
          <select value={previewMa?.id || ''} onChange={e => {
            const emp = employees.find(x => x.id === Number(e.target.value));
            if (emp) setPreviewMa(emp);
          }}
            className="px-3 py-1.5 rounded-lg border text-sm outline-none max-w-[200px]"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-primary, #1A1A2E)', background: 'var(--bg-card, #fff)' }}>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>

          <button onClick={() => setShowGrid(!showGrid)}
            className={`p-1.5 rounded-lg border transition-colors ${showGrid ? 'border-[#338edc] bg-[#338edc]/10 text-[#338edc]' : 'text-[var(--text-tertiary,#6B7280)]'}`}
            style={{ borderColor: showGrid ? '#338edc' : 'var(--border-default, #E5E7EB)' }}
            title={t('ausweisEditor.gridToggle', 'Hilfslinien')}>
            <Grid3X3 size={16} />
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button onClick={resetToDefault}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors hover:bg-[var(--bg-inset,#F8F9FC)]"
              style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
              <RotateCcw size={12} />{t('ausweisEditor.reset', 'Zurücksetzen')}
            </button>
            <button onClick={saveLayout} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] disabled:opacity-60">
              <Save size={12} />{saving ? '...' : t('ausweisEditor.save', 'Speichern')}
            </button>
          </div>
        </div>

        {/* Preview image */}
        <div className="relative bg-[var(--bg-inset,#F8F9FC)] border rounded-xl p-6 flex items-center justify-center min-h-[350px]"
          style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
          {previewLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10 rounded-xl">
              <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {previewImg ? (
            <div className="relative">
              <img src={previewImg} alt="Preview" className="max-w-full rounded-lg border border-[var(--border-card,#E5E7EB)]"
                style={{ maxHeight: '420px' }} />
              {showGrid && (
                <div className="absolute inset-0 pointer-events-none rounded-lg"
                  style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 49px, rgba(51,142,220,0.15) 50px), repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(51,142,220,0.15) 50px)', backgroundSize: '50px 50px' }} />
              )}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('ausweisEditor.noPreview', 'Vorschau wird geladen...')}
            </p>
          )}
        </div>

        {/* Layout management */}
        <div className="mt-4 border rounded-xl p-4" style={{ borderColor: 'var(--border-default, #E5E7EB)', background: 'var(--bg-card, #fff)' }}>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('ausweisEditor.savedLayouts', 'Gespeicherte Layouts')}
            </h3>
            <button onClick={createLayout}
              className="ml-auto flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium hover:bg-[var(--bg-inset,#F8F9FC)]"
              style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
              <Plus size={12} />{t('ausweisEditor.newLayout', 'Neu')}
            </button>
          </div>
          <div className="space-y-2">
            {layouts.map(l => (
              <div key={l.id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${activeLayoutId === l.id ? 'border-[#338edc] bg-[#338edc]/5' : 'hover:bg-[var(--bg-inset,#F8F9FC)]'}`}
                style={{ borderColor: activeLayoutId === l.id ? '#338edc' : 'var(--border-default, #E5E7EB)' }}
                onClick={() => loadLayout(l.id)}>
                <span className="text-sm font-medium flex-1" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {l.name}
                </span>
                {l.ist_aktiv && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#338edc]/10 text-[#338edc]">
                    {t('ausweisEditor.active', 'Aktiv')}
                  </span>
                )}
                {!l.ist_aktiv && (
                  <button onClick={e => { e.stopPropagation(); setActive(l.id); }}
                    className="p-1 rounded text-[var(--text-muted,#9CA3AF)] hover:text-[#338edc]" title={t('ausweisEditor.setActive', 'Als Standard')}>
                    <Star size={14} />
                  </button>
                )}
                {!l.ist_aktiv && (
                  <button onClick={e => { e.stopPropagation(); deleteLayout(l.id); }}
                    className="p-1 rounded text-[var(--text-muted,#9CA3AF)] hover:text-red-500" title={t('common.delete', 'Löschen')}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Right: Config Panel ──────────────────────────── */}
      <div className="w-full lg:w-[380px] lg:max-h-[calc(100vh-180px)] overflow-y-auto space-y-3 pr-1">
        {/* Layout name */}
        <TextField label={t('ausweisEditor.layoutName', 'Layout-Name')} value={layoutName}
          onChange={v => setLayoutName(v)} />

        {/* 1. Gradient */}
        <Section title={t('ausweisEditor.gradient', 'Gradient-Balken')}>
          <SliderField label={t('ausweisEditor.height', 'Höhe')} value={g.height || 83} min={40} max={150} onChange={v => updateField('gradient', 'height', v)} />
          <ColorField label={t('ausweisEditor.colorLeft', 'Farbe links')} value={g.colorLeft} onChange={v => updateField('gradient', 'colorLeft', v)} />
          <ColorField label={t('ausweisEditor.colorMid', 'Farbe mitte')} value={g.colorMid} onChange={v => updateField('gradient', 'colorMid', v)} />
          <ColorField label={t('ausweisEditor.colorRight', 'Farbe rechts')} value={g.colorRight} onChange={v => updateField('gradient', 'colorRight', v)} />
          <ToggleField label={t('ausweisEditor.logoEnabled', 'Logo anzeigen')} value={logo.enabled !== false} onChange={v => updateField('logo', 'enabled', v)} />
          {logo.enabled !== false && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <SliderField label={t('ausweisEditor.logoMaxW', 'Logo Breite')} value={logo.maxW ?? 110} min={30} max={400} onChange={v => updateField('logo', 'maxW', v)} />
                <SliderField label={t('ausweisEditor.logoMaxH', 'Logo Höhe')} value={logo.maxH ?? 36} min={15} max={200} onChange={v => updateField('logo', 'maxH', v)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SliderField label="Logo X" value={logo.x ?? 50} min={0} max={1012} onChange={v => updateField('logo', 'x', v)} />
                <SliderField label="Logo Y" value={logo.y ?? 24} min={0} max={150} onChange={v => updateField('logo', 'y', v)} />
              </div>
            </>
          )}
          <TextField label={t('ausweisEditor.headerTextLabel', 'Header-Text')} value={ht.text} onChange={v => updateField('headerText', 'text', v)} />
          <SliderField label={t('ausweisEditor.headerFontSize', 'Header Schriftgröße')} value={ht.fontSize || 20} min={8} max={72} onChange={v => updateField('headerText', 'fontSize', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="Header X" value={ht.x ?? 750} min={0} max={1012} onChange={v => updateField('headerText', 'x', v)} />
            <SliderField label="Header Y" value={ht.y ?? 42} min={0} max={150} onChange={v => updateField('headerText', 'y', v)} />
          </div>
        </Section>

        {/* 2. Photo */}
        <Section title={t('ausweisEditor.photo', 'Foto')}>
          <SliderField label={t('ausweisEditor.radius', 'Größe (Radius)')} value={photo.radius || 70} min={30} max={150} onChange={v => updateField('photo', 'radius', v)} />
          <SelectField label={t('ausweisEditor.shape', 'Form')} value={photo.shape || 'circle'}
            options={[{ value: 'circle', label: t('ausweisEditor.circle', 'Rund') }, { value: 'square', label: t('ausweisEditor.square', 'Quadratisch') }]}
            onChange={v => updateField('photo', 'shape', v)} />
          <SliderField label={t('ausweisEditor.borderWidth', 'Rahmen')} value={photo.borderWidth ?? 3} min={0} max={8} onChange={v => updateField('photo', 'borderWidth', v)} />
          <ColorField label={t('ausweisEditor.borderColor', 'Rahmenfarbe')} value={photo.borderColor} onChange={v => updateField('photo', 'borderColor', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="X" value={photo.x ?? 120} min={0} max={1012} onChange={v => updateField('photo', 'x', v)} />
            <SliderField label="Y" value={photo.y ?? 200} min={0} max={638} onChange={v => updateField('photo', 'y', v)} />
          </div>
        </Section>

        {/* 3. Name */}
        <Section title={t('ausweisEditor.nameSection', 'Name')}>
          <SliderField label={t('ausweisEditor.fontSize', 'Schriftgröße')} value={name.fontSize || 28} min={8} max={72} onChange={v => updateField('name', 'fontSize', v)} />
          <SelectField label={t('ausweisEditor.fontWeight', 'Schriftstärke')} value={String(name.fontWeight || 700)}
            options={[{ value: '400', label: 'Normal' }, { value: '500', label: 'Medium' }, { value: '700', label: 'Bold' }]}
            onChange={v => updateField('name', 'fontWeight', Number(v))} />
          <ColorField label={t('ausweisEditor.color', 'Farbe')} value={name.color} onChange={v => updateField('name', 'color', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="X" value={name.x ?? 226} min={0} max={1012} onChange={v => updateField('name', 'x', v)} />
            <SliderField label="Y" value={name.y ?? 170} min={0} max={638} onChange={v => updateField('name', 'y', v)} />
          </div>
        </Section>

        {/* 4. Personalnummer */}
        <Section title={t('ausweisEditor.personnelNr', 'Personalnummer')} defaultOpen={false}>
          <SliderField label={t('ausweisEditor.labelValueGap', 'Abstand Label ↔ Wert')} value={config.labelValueGap ?? 4} min={0} max={20} onChange={v => setConfig(prev => ({ ...prev, labelValueGap: v }))} />
          <TextField label={t('ausweisEditor.label', 'Label')} value={pnr.label} onChange={v => updateField('personalnummer', 'label', v)} />
          <SliderField label={t('ausweisEditor.labelFontSize', 'Label Schriftgröße')} value={pnr.labelFontSize || 14} min={8} max={72} onChange={v => updateField('personalnummer', 'labelFontSize', v)} />
          <ColorField label={t('ausweisEditor.labelColor', 'Label Farbe')} value={pnr.labelColor} onChange={v => updateField('personalnummer', 'labelColor', v)} />
          <SliderField label={t('ausweisEditor.valueFontSize', 'Wert Schriftgröße')} value={pnr.valueFontSize || 18} min={8} max={72} onChange={v => updateField('personalnummer', 'valueFontSize', v)} />
          <ColorField label={t('ausweisEditor.valueColor', 'Wert Farbe')} value={pnr.valueColor} onChange={v => updateField('personalnummer', 'valueColor', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="X" value={pnr.x ?? 226} min={0} max={1012} onChange={v => updateField('personalnummer', 'x', v)} />
            <SliderField label="Y" value={pnr.y ?? 198} min={0} max={638} onChange={v => updateField('personalnummer', 'y', v)} />
          </div>
        </Section>

        {/* 5. Abteilung */}
        <Section title={t('ausweisEditor.department', 'Abteilung')} defaultOpen={false}>
          <TextField label={t('ausweisEditor.label', 'Label')} value={abt.label} onChange={v => updateField('abteilung', 'label', v)} />
          <SliderField label={t('ausweisEditor.labelFontSize', 'Label Schriftgröße')} value={abt.labelFontSize || 14} min={8} max={72} onChange={v => updateField('abteilung', 'labelFontSize', v)} />
          <ColorField label={t('ausweisEditor.labelColor', 'Label Farbe')} value={abt.labelColor} onChange={v => updateField('abteilung', 'labelColor', v)} />
          <SliderField label={t('ausweisEditor.valueFontSize', 'Wert Schriftgröße')} value={abt.valueFontSize || 18} min={8} max={72} onChange={v => updateField('abteilung', 'valueFontSize', v)} />
          <ColorField label={t('ausweisEditor.valueColor', 'Wert Farbe')} value={abt.valueColor} onChange={v => updateField('abteilung', 'valueColor', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="X" value={abt.x ?? 380} min={0} max={1012} onChange={v => updateField('abteilung', 'x', v)} />
            <SliderField label="Y" value={abt.y ?? 198} min={0} max={638} onChange={v => updateField('abteilung', 'y', v)} />
          </div>
        </Section>

        {/* 6. Barcode */}
        <Section title={t('ausweisEditor.barcode', 'Barcode')}>
          <SliderField label={t('ausweisEditor.barcodeHeight', 'Höhe')} value={bc.height || 80} min={30} max={200} onChange={v => updateField('barcode', 'height', v)} />
          <SliderField label={t('ausweisEditor.barcodeScale', 'Strichbreite')} value={bc.barcodeScale || 2.2} min={1} max={8} step={0.1} unit="" onChange={v => updateField('barcode', 'barcodeScale', v)} />
          <ToggleField label={t('ausweisEditor.centerH', 'Horizontal zentrieren')} value={bc.centerH !== false} onChange={v => updateField('barcode', 'centerH', v)} />
          <ToggleField label={t('ausweisEditor.verticalCenter', 'Vertikal zentrieren')} value={bc.verticalCenter !== false} onChange={v => updateField('barcode', 'verticalCenter', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="X" value={bc.x ?? 400} min={0} max={1012} onChange={v => updateField('barcode', 'x', v)} disabled={bc.centerH !== false} />
            <SliderField label="Y" value={bc.y ?? 430} min={0} max={638} onChange={v => updateField('barcode', 'y', v)} disabled={bc.verticalCenter !== false} />
          </div>
          <ToggleField label={t('ausweisEditor.numberVisible', 'Nummer anzeigen')} value={bc.numberVisible !== false} onChange={v => updateField('barcode', 'numberVisible', v)} />
          {bc.numberVisible !== false && (
            <>
              <SliderField label={t('ausweisEditor.numberFontSize', 'Nummer Schriftgröße')} value={bc.numberFontSize || 13} min={8} max={72} onChange={v => updateField('barcode', 'numberFontSize', v)} />
              <ColorField label={t('ausweisEditor.numberColor', 'Nummer Farbe')} value={bc.numberColor} onChange={v => updateField('barcode', 'numberColor', v)} />
              <SliderField label={t('ausweisEditor.numberGap', 'Abstand zum Barcode')} value={bc.numberGap ?? 4} min={0} max={20} onChange={v => updateField('barcode', 'numberGap', v)} />
              <ToggleField label={t('ausweisEditor.centerUnderBarcode', 'Unter Barcode zentrieren')} value={bc.numberCenterUnderBarcode !== false} onChange={v => updateField('barcode', 'numberCenterUnderBarcode', v)} />
              {bc.numberCenterUnderBarcode === false && (
                <div className="grid grid-cols-2 gap-2">
                  <SliderField label="Nr. X" value={bc.numberX ?? 450} min={0} max={1012} onChange={v => updateField('barcode', 'numberX', v)} />
                  <SliderField label="Nr. Y" value={bc.numberY ?? 490} min={0} max={638} onChange={v => updateField('barcode', 'numberY', v)} />
                </div>
              )}
            </>
          )}
        </Section>

        {/* 7. Footer */}
        <Section title={t('ausweisEditor.footer', 'Footer')}>
          <SliderField label={t('ausweisEditor.height', 'Höhe')} value={ft.height || 55} min={30} max={100} onChange={v => updateField('footer', 'height', v)} />
          <ColorField label={t('ausweisEditor.bgColor', 'Hintergrund')} value={ft.bgColor} onChange={v => updateField('footer', 'bgColor', v)} />
          <TextField label={t('ausweisEditor.textLeft', 'Text links')} value={ft.textLeft} onChange={v => updateField('footer', 'textLeft', v)} />
          <SliderField label={t('ausweisEditor.textLeftSize', 'Text links Größe')} value={ft.textLeftSize || 13} min={8} max={72} onChange={v => updateField('footer', 'textLeftSize', v)} />
          <ColorField label={t('ausweisEditor.textLeftColor', 'Text links Farbe')} value={ft.textLeftColor} onChange={v => updateField('footer', 'textLeftColor', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="Links X" value={ft.textLeftX ?? 50} min={0} max={1012} onChange={v => updateField('footer', 'textLeftX', v)} />
            <SliderField label="Links Y" value={ft.textLeftY ?? 611} min={0} max={638} onChange={v => updateField('footer', 'textLeftY', v)} />
          </div>
          <TextareaField label={t('ausweisEditor.textRight', 'Text rechts')} value={ft.textRight} onChange={v => updateField('footer', 'textRight', v)} />
          <SliderField label={t('ausweisEditor.textRightSize', 'Text rechts Größe')} value={ft.textRightSize || 11} min={8} max={72} onChange={v => updateField('footer', 'textRightSize', v)} />
          <ColorField label={t('ausweisEditor.textRightColor', 'Text rechts Farbe')} value={ft.textRightColor} onChange={v => updateField('footer', 'textRightColor', v)} />
          <div className="grid grid-cols-2 gap-2">
            <SliderField label="Rechts X" value={ft.textRightX ?? 962} min={0} max={1012} onChange={v => updateField('footer', 'textRightX', v)} />
            <SliderField label="Rechts Y" value={ft.textRightY ?? 611} min={0} max={638} onChange={v => updateField('footer', 'textRightY', v)} />
          </div>
        </Section>

        {/* 8. Extra text fields */}
        <Section title={t('ausweisEditor.extraFields', 'Zusätzliche Textfelder')} defaultOpen={false}>
          {(config.extraFields || []).map((ef, i) => (
            <div key={i} className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'var(--border-default, #E5E7EB)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium" style={{ color: 'var(--text-secondary, #4B5563)' }}>
                  {t('ausweisEditor.field', 'Feld')} {i + 1}
                </span>
                <button onClick={() => {
                  const updated = [...(config.extraFields || [])];
                  updated.splice(i, 1);
                  setConfig(prev => ({ ...prev, extraFields: updated }));
                }} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
              </div>
              <TextField label={t('ausweisEditor.text', 'Text')} value={ef.text}
                placeholder="{{name}}, {{personalnummer}}, ..."
                onChange={v => {
                  const updated = [...(config.extraFields || [])];
                  updated[i] = { ...updated[i], text: v };
                  setConfig(prev => ({ ...prev, extraFields: updated }));
                }} />
              <div className="grid grid-cols-2 gap-2">
                <SliderField label="X" value={ef.x || 0} min={0} max={1012} onChange={v => {
                  const updated = [...(config.extraFields || [])];
                  updated[i] = { ...updated[i], x: v };
                  setConfig(prev => ({ ...prev, extraFields: updated }));
                }} />
                <SliderField label="Y" value={ef.y || 0} min={0} max={638} onChange={v => {
                  const updated = [...(config.extraFields || [])];
                  updated[i] = { ...updated[i], y: v };
                  setConfig(prev => ({ ...prev, extraFields: updated }));
                }} />
              </div>
              <SliderField label={t('ausweisEditor.fontSize', 'Schriftgröße')} value={ef.fontSize || 14} min={8} max={72} onChange={v => {
                const updated = [...(config.extraFields || [])];
                updated[i] = { ...updated[i], fontSize: v };
                setConfig(prev => ({ ...prev, extraFields: updated }));
              }} />
              <ColorField label={t('ausweisEditor.color', 'Farbe')} value={ef.color || '#1A1A2E'} onChange={v => {
                const updated = [...(config.extraFields || [])];
                updated[i] = { ...updated[i], color: v };
                setConfig(prev => ({ ...prev, extraFields: updated }));
              }} />
            </div>
          ))}
          <button onClick={() => {
            const updated = [...(config.extraFields || []), { text: '', x: 50, y: 300, fontSize: 14, fontWeight: 400, color: '#1A1A2E' }];
            setConfig(prev => ({ ...prev, extraFields: updated }));
          }}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed text-xs font-medium hover:bg-[var(--bg-inset,#F8F9FC)]"
            style={{ borderColor: 'var(--border-default, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
            <Plus size={12} />{t('ausweisEditor.addField', 'Textfeld hinzufügen')}
          </button>
          <p className="text-[10px]" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
            {t('ausweisEditor.varsHint', 'Variablen: {{name}}, {{personalnummer}}, {{abteilung}}, {{barcode}}, {{position}}')}
          </p>
        </Section>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl shadow-sm px-4 py-3 text-sm text-[var(--text-primary,#1A1A2E)] flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-500" />{toast}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export default function AusweisePage() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useTabParam('mitarbeiter');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [previewImg, setPreviewImg] = useState(null);
  const [previewEmp, setPreviewEmp] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [toast, setToast] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (activeTab !== 'mitarbeiter') return;
    setLoading(true);
    fetch(`${API}/api/ausweis/mitarbeiter`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setEmployees(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }
  }, [toast]);

  const kpiTotal = employees.length;
  const kpiPhoto = employees.filter(e => e.has_photo).length;
  const kpiBarcode = employees.filter(e => e.has_barcode).length;

  const filtered = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(s) ||
      (e.abteilung_name || e.department || '').toLowerCase().includes(s) ||
      (e.berufsbezeichnung || '').toLowerCase().includes(s)
    );
  });
  const sorted = [...filtered].sort((a, b) => {
    const va = (sortBy === 'name' ? a.name : (a.abteilung_name || a.department || '')) || '';
    const vb = (sortBy === 'name' ? b.name : (b.abteilung_name || b.department || '')) || '';
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  const allSelected = sorted.length > 0 && sorted.every(e => selected.has(e.id));
  const toggleAll = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(sorted.map(e => e.id))); };
  const toggleOne = (id) => { const s = new Set(selected); if (s.has(id)) s.delete(id); else s.add(id); setSelected(s); };

  const generateSingle = async (maId) => {
    setGenerating(maId);
    try {
      const res = await fetch(`${API}/api/ausweis/generieren`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_id: maId }),
      });
      const data = await res.json();
      if (data.success && data.download_url) {
        window.open(`${API}${data.download_url}`, '_blank');
        setToast(t('ausweis.generated') || 'Ausweis generiert');
      }
    } catch { setToast('Fehler bei Generierung'); }
    setGenerating(null);
  };

  const generatePdf = async (maId) => {
    setGenerating(`pdf-${maId}`);
    try {
      const res = await fetch(`${API}/api/ausweis/generieren-pdf`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_id: maId }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'Ausweis.pdf';
        a.click();
        URL.revokeObjectURL(url);
        setToast(t('ausweis.pdfGenerated', 'PDF generiert'));
      }
    } catch { setToast('Fehler bei PDF-Generierung'); }
    setGenerating(null);
  };

  const generateBulk = async () => {
    if (selected.size === 0) return;
    setGenerating('bulk');
    try {
      const res = await fetch(`${API}/api/ausweis/generieren/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_ids: [...selected] }),
      });
      const data = await res.json();
      if (data.success && data.download_url) {
        window.open(`${API}${data.download_url}`, '_blank');
        setToast(`${data.count} ${t('ausweis.generated') || 'Ausweise generiert'}`);
      }
    } catch { setToast('Fehler bei Bulk-Generierung'); }
    setGenerating(null);
  };

  const openPreview = async (emp) => {
    setPreviewEmp(emp);
    setPreviewImg(null);
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API}/api/ausweis/vorschau`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mitarbeiter_id: emp.id }),
      });
      const data = await res.json();
      if (data.preview) setPreviewImg(data.preview);
    } catch {}
    setPreviewLoading(false);
  };

  const SortHeader = ({ field, children }) => (
    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider cursor-pointer select-none hover:text-[var(--text-secondary,#4B5563)]"
      onClick={() => { if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(field); setSortDir('asc'); } }}>
      <span className="inline-flex items-center gap-1">{children}{sortBy === field && <span className="text-[#338edc]">{sortDir === 'asc' ? '↑' : '↓'}</span>}</span>
    </th>
  );

  const tabs = [
    { key: 'mitarbeiter', label: t('ausweis.tabEmployees', 'Mitarbeiter'), icon: <Users size={14} /> },
    { key: 'editor', label: t('ausweis.tabEditor', 'Layout-Editor'), icon: <Sliders size={14} /> },
  ];

  return (
    <div className="p-6 md:p-8 space-y-6">
      <PageHeader
        title={t('ausweis.title') || 'Mitarbeiterausweise'}
        subtitle={t('ausweis.subtitle') || 'Mitarbeiterausweise generieren und drucken'}
      />

      <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {/* ═══ Tab: Mitarbeiter ═══ */}
      {activeTab === 'mitarbeiter' && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-[var(--text-tertiary,#6B7280)]" />
                <p className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">{t('ausweis.kpiTotal') || 'Mitarbeiter gesamt'}</p>
              </div>
              <p className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">{kpiTotal}</p>
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Camera size={16} className="text-[var(--text-tertiary,#6B7280)]" />
                <p className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">{t('ausweis.kpiWithPhoto') || 'Mit Foto'}</p>
              </div>
              <p className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">{kpiPhoto}</p>
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard size={16} className="text-[var(--text-tertiary,#6B7280)]" />
                <p className="text-xs font-medium text-[var(--text-secondary,#4B5563)] uppercase tracking-wider">{t('ausweis.kpiWithBarcode') || 'Mit Barcode'}</p>
              </div>
              <p className="text-2xl font-bold text-[var(--text-primary,#1A1A2E)]">{kpiBarcode}</p>
            </div>
          </div>

          {selected.size > 0 && (
            <div className="flex items-center gap-3 bg-[var(--primary-light,#EBF4FD)] border border-[#338edc]/20 rounded-xl px-4 py-3">
              <span className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                {(t('ausweis.selected') || '{{count}} ausgewählt').replace('{{count}}', selected.size)}
              </span>
              <button onClick={generateBulk} disabled={generating === 'bulk'}
                className="ml-auto px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-60 flex items-center gap-2">
                {generating === 'bulk' ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Download size={14} />}
                {t('ausweis.generateAll') || 'Alle generieren'}
              </button>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)]" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t('common.search') || 'Suchen...'}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:border-[#338edc] focus:ring-2 focus:ring-[rgba(51,142,220,0.1)] outline-none" />
            </div>
            <span className="text-xs text-[var(--text-tertiary,#6B7280)]">CR-80 (85,6 × 54 mm)</span>
          </div>

          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-default,#E5E7EB)]">
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll}
                        className="w-4 h-4 rounded border-[var(--border-default,#D1D5DB)] text-[#338edc] focus:ring-[#338edc]" />
                    </th>
                    <SortHeader field="name">{t('ausweis.employee') || 'Mitarbeiter'}</SortHeader>
                    <SortHeader field="department">{t('ausweis.department') || 'Abteilung'}</SortHeader>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider">{t('ausweis.barcode') || 'Barcode'}</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-default,#F3F4F6)]">
                  {loading ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center">
                      <div className="inline-block w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
                    </td></tr>
                  ) : sorted.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-[var(--text-tertiary,#6B7280)]">
                      {t('common.noResults') || 'Keine Ergebnisse'}
                    </td></tr>
                  ) : sorted.map(emp => (
                    <tr key={emp.id} className={`hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors ${selected.has(emp.id) ? 'bg-[var(--bg-selected-row,#EBF4FD)]' : ''}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(emp.id)} onChange={() => toggleOne(emp.id)}
                          className="w-4 h-4 rounded border-[var(--border-default,#D1D5DB)] text-[#338edc] focus:ring-[#338edc]" />
                      </td>
                      <td className="px-4 py-3">
                        <MitarbeiterListItem mitarbeiter={emp} size="sm" apiBase={API} />
                      </td>
                      <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)]">{emp.abteilung_name || emp.department || '—'}</td>
                      <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)] font-mono text-xs">{emp.barcode || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {emp.has_photo ? <CheckCircle size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-red-400" />}
                          {emp.has_barcode ? <CheckCircle size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-red-400" />}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openPreview(emp)} title={t('ausweis.preview') || 'Vorschau'}
                            className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                            <Eye size={16} />
                          </button>
                          <button onClick={() => generateSingle(emp.id)} disabled={generating === emp.id}
                            className="px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-xs font-medium hover:bg-[#2b7bc4] disabled:opacity-60 flex items-center gap-1.5"
                            title="PNG">
                            {generating === emp.id ? <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Download size={12} />}
                            PNG
                          </button>
                          <button onClick={() => generatePdf(emp.id)} disabled={generating === `pdf-${emp.id}`}
                            className="px-3 py-1.5 rounded-lg border border-[#338edc] text-[#338edc] text-xs font-medium hover:bg-[#338edc]/5 disabled:opacity-60 flex items-center gap-1.5"
                            title={t('ausweis.pdfPrint', 'PDF (Druckqualität)')}>
                            {generating === `pdf-${emp.id}` ? <span className="inline-block w-3 h-3 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" /> : <FileText size={12} />}
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {previewEmp && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPreviewEmp(null)}>
              <div className="bg-[var(--bg-card,#fff)] rounded-xl shadow-sm w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-[var(--border-default,#E5E7EB)] flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">{previewEmp.name}</h2>
                  <button onClick={() => setPreviewEmp(null)} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]">
                    <XCircle size={20} />
                  </button>
                </div>
                <div className="p-6 flex justify-center min-h-[300px] items-center">
                  {previewLoading ? (
                    <div className="inline-block w-8 h-8 border-3 border-[#338edc] border-t-transparent rounded-full animate-spin" />
                  ) : previewImg ? (
                    <img src={previewImg} alt={`Ausweis ${previewEmp.name}`}
                      className="max-w-full border border-[var(--border-card,#E5E7EB)] rounded-lg"
                      style={{ maxHeight: '400px' }} />
                  ) : (
                    <p className="text-[var(--text-tertiary,#6B7280)]">{t('ausweis.previewError') || 'Vorschau konnte nicht geladen werden'}</p>
                  )}
                </div>
                <div className="px-6 py-4 border-t border-[var(--border-default,#E5E7EB)] flex items-center justify-end gap-3">
                  <button onClick={() => setPreviewEmp(null)}
                    className="px-4 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
                    {t('common.close') || 'Schließen'}
                  </button>
                  <button onClick={() => { generateSingle(previewEmp.id); setPreviewEmp(null); }} disabled={generating === previewEmp.id}
                    className="px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-60 flex items-center gap-2">
                    <Download size={14} />
                    PNG
                  </button>
                  <button onClick={() => { generatePdf(previewEmp.id); setPreviewEmp(null); }} disabled={generating === `pdf-${previewEmp.id}`}
                    className="px-4 py-2 rounded-lg border border-[#338edc] text-[#338edc] text-sm font-medium hover:bg-[#338edc]/5 disabled:opacity-60 flex items-center gap-2">
                    <FileText size={14} />
                    {t('ausweis.pdfPrint', 'PDF (Druckqualität)')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Tab: Layout-Editor ═══ */}
      {activeTab === 'editor' && <LayoutEditor t={t} />}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl shadow-sm px-4 py-3 text-sm text-[var(--text-primary,#1A1A2E)] flex items-center gap-2 animate-in slide-in-from-bottom-2">
          <CheckCircle size={16} className="text-emerald-500" />{toast}
        </div>
      )}
    </div>
  );
}
